import { merchantWallTimeToUtcIso } from '@lib/merchant-tz';
import { minutesOfDay } from '../board/flowCopy';

/**
 * Pure model of the dispatch run card (#2475): which label the run button
 * carries and where the gate timeline puts its marks. Gate times are tenant
 * `HH:mm` wall times on the batch day; every comparison is between instants,
 * never between strings.
 */

/** `beforeCutoff` = today, before the packaging cutoff: run now, but suggest waiting. */
export type RunButtonVariant = 'beforeCutoff' | 'now' | 'early';

/** The timeline starts this long before the earlier gate (08:00 for the HGS 10:00 default). */
const TIMELINE_LEAD_MINUTES = 120;
const MINUTES_PER_DAY = 24 * 60;

function wallInstantMs(date: string, hhmm: string): number {
  return new Date(merchantWallTimeToUtcIso(`${date}T${hhmm}:00.000`)).getTime();
}

function formatMinutes(minutes: number): string {
  const wrapped = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function runButtonVariant({
  date,
  today,
  nowMs,
  packagingCutoff,
}: {
  date: string;
  today: string;
  nowMs: number;
  /** `null` when the delivery flow failed to load: no time is suggested. */
  packagingCutoff: string | null;
}): RunButtonVariant {
  if (date > today) return 'early';
  if (date < today || packagingCutoff === null) return 'now';
  return nowMs < wallInstantMs(date, packagingCutoff) ? 'beforeCutoff' : 'now';
}

export interface TimelineGeometry {
  /** `HH:mm` of the left end. */
  startLabel: string;
  /** Packaging-cutoff mark, 0–100 from the left end; `null` without one (#2631). */
  packagingPct: number | null;
  /** The "now" pointer, clamped to 0–100; `null` on any day but today. */
  pointerPct: number | null;
}

export function timelineGeometry({
  date,
  isToday,
  nowMs,
  packagingCutoff,
  dispatchCutoff,
}: {
  date: string;
  isToday: boolean;
  nowMs: number;
  /** `null` when there is no dispatch window: the line starts before the dispatch cutoff. */
  packagingCutoff: string | null;
  dispatchCutoff: string;
}): TimelineGeometry {
  const packagingMs = packagingCutoff === null ? null : wallInstantMs(date, packagingCutoff);
  const dispatchMs = wallInstantMs(date, dispatchCutoff);
  const leadMs = TIMELINE_LEAD_MINUTES * 60_000;
  const startMs = Math.min(packagingMs ?? dispatchMs, dispatchMs) - leadMs;
  const endMs = Math.max(packagingMs ?? dispatchMs, dispatchMs);
  const span = endMs - startMs;
  const pct = (ms: number) => Math.min(100, Math.max(0, ((ms - startMs) / span) * 100));
  const earlier = Math.min(
    packagingCutoff === null ? Infinity : minutesOfDay(packagingCutoff),
    minutesOfDay(dispatchCutoff),
  );
  return {
    startLabel: formatMinutes(earlier - TIMELINE_LEAD_MINUTES),
    packagingPct: packagingMs === null ? null : pct(packagingMs),
    pointerPct: isToday ? pct(nowMs) : null,
  };
}
